import React, { useEffect, useState } from 'react';
import {
  Dialog,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch
} from '@mui/material';
import { Edit } from '@mui/icons-material';
import { api as axios } from '../../api';
import url from '../../serverUrl';
import { logoWrapper } from '../../utils';
import TreasuryLockStatusFields from './TreasuryLockStatusFields';

function EditWrapToken({ token, open, setOpen }) {
  const [updatedToken, setUpdatedToken] = useState(token);
  const [logo, setLogo] = useState(updatedToken?.logo);
  const [statusChecked, setStatusChecked] = useState(token?.status);

  useEffect(() => {
    setUpdatedToken(token);
    setLogo(token.logo);
    setStatusChecked(token?.status);
  }, [token]);

  const handleClose = () => setOpen(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUpdatedToken({ ...updatedToken, [name]: value });
  };

  const saveToken = () => {
    const bodyData = {
      id: updatedToken.id,
      type: 'wrap',
      symbol: updatedToken.symbol,
      name: updatedToken.name,
      description: updatedToken.description,
      link: updatedToken.link,
      utility_type: updatedToken.utility_type,
      issuer_field: updatedToken.issuer_field,
      mint_burn_model: updatedToken.mint_burn_model,
      redemption_type: updatedToken.redemption_type,
      mechanism: updatedToken.mechanism,
      risk_profile: updatedToken.risk_profile,
      collateral_type: updatedToken.collateral_type,
      underlying_asset: updatedToken.underlying_asset,
      logo: updatedToken.logo,
      status: statusChecked
    };

    axios
      .put(`${url}/crypto/updateToken`, bodyData)
      .then((res) => alert(JSON.stringify(res.data)))
      .catch((err) => alert(JSON.stringify(err)));
  };

  const handleLogoUpload = (e) => {
    const pic = e.target.files[0];
    setLogo(URL.createObjectURL(pic));

    const formData = new FormData();
    formData.append('image', pic);
    formData.append('fileName', pic.name);

    axios.post(`${url}/hootdex/token-logo-upload`, formData).then((res) => {
      if (res.data.status === 'ok') {
        setUpdatedToken({
          ...updatedToken,
          logo: `${url}/hootdex/images/${res.data.fileName}`
        });
      }
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      PaperProps={{
        style: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
          width: '100%'
        }
      }}
      sx={{ zIndex: 2002 }}
    >
      <div style={{ backgroundColor: 'whitesmoke', padding: '5rem' }}>
        {/* Logo */}
        <div style={{ position: 'relative' }}>
          <img src={logoWrapper(logo, token)} width={150} alt={token?.name} />
          <input
            id="fileInput"
            type="file"
            hidden
            onChange={handleLogoUpload}
          />
          <label
            htmlFor="fileInput"
            style={{ position: 'absolute', bottom: '20px', cursor: 'pointer' }}
          >
            <Edit />
          </label>
        </div>

        {/* Status */}
        <p>
          Status {statusChecked ? 'Active' : 'Inactive'}:
          <Switch
            checked={statusChecked}
            onChange={(e) => setStatusChecked(e.target.checked)}
            inputProps={{ 'aria-label': 'controlled' }}
          />
        </p>

        {/* Basic fields */}
        <TextField
          fullWidth
          label="Name"
          margin="normal"
          name="name"
          value={updatedToken.name ?? ''}
          onChange={handleChange}
        />
        <TextField
          fullWidth
          label="Symbol"
          margin="normal"
          name="symbol"
          value={updatedToken.symbol ?? ''}
          onChange={handleChange}
        />
        <TextField
          fullWidth
          label="Description"
          margin="normal"
          multiline
          rows={4}
          name="description"
          value={updatedToken.description ?? ''}
          onChange={handleChange}
        />

        {/* Dropdowns for WRAP token */}
        <FormControl fullWidth margin="normal">
          <InputLabel id="utility-label">Utility Type</InputLabel>
          <Select
            labelId="utility-label"
            label="Utility Type"
            value={updatedToken.utility_type ?? ''}
            name="utility_type"
            onChange={handleChange}
            MenuProps={{ disablePortal: true }}
          >
            <MenuItem value="Cross-Chain">Cross-Chain</MenuItem>
            <MenuItem value="DeFi">DeFi</MenuItem>
            <MenuItem value="Collateral">Collateral</MenuItem>
            <MenuItem value="Settlement">Settlement</MenuItem>
            <MenuItem value="Interoperability">Interoperability</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth margin="normal">
          <InputLabel id="issuer-label">Issuer Field</InputLabel>
          <Select
            labelId="issuer-label"
            label="Issuer Field"
            value={updatedToken.issuer_field ?? ''}
            name="issuer_field"
            onChange={handleChange}
            MenuProps={{ disablePortal: true }}
          >
            <MenuItem value="Legal Name">Legal Name</MenuItem>
            <MenuItem value="Short Name">Short Name</MenuItem>
            <MenuItem value="Token Symbol">Token Symbol</MenuItem>
            <MenuItem value="Digital ID">Digital ID</MenuItem>
            <MenuItem value="Vault Type">Vault Type</MenuItem>
            <MenuItem value="Vault Operator">Vault Operator</MenuItem>
            <MenuItem value="Issuer-Managed Treasury">Issuer Treasury</MenuItem>
            <MenuItem value="DAT-Managed Treasury">DAT Treasury</MenuItem>
            <MenuItem value="Smart-Contract Autonomous Treasury">
              Smart-Contract Treasury
            </MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth margin="normal">
          <InputLabel id="mintburn-label">Mint/Burn Model</InputLabel>
          <Select
            labelId="mintburn-label"
            label="Mint/Burn Model"
            value={updatedToken.mint_burn_model ?? ''}
            name="mint_burn_model"
            onChange={handleChange}
            MenuProps={{ disablePortal: true }}
          >
            <MenuItem value="Mint-On-Deposit">Mint-On-Deposit</MenuItem>
            <MenuItem value="Burn-On-Withdrawal">Burn-On-Withdrawal</MenuItem>
            <MenuItem value="Smart-Contract Vault">
              Smart-Contract Vault
            </MenuItem>
            <MenuItem value="Bridge">Bridge</MenuItem>
            <MenuItem value="DAT-Vault">DAT-Vault</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth margin="normal">
          <InputLabel id="redemption-label">Redemption Type</InputLabel>
          <Select
            labelId="redemption-label"
            label="Redemption Type"
            value={updatedToken.redemption_type ?? ''}
            name="redemption_type"
            onChange={handleChange}
            MenuProps={{ disablePortal: true }}
          >
            <MenuItem value="Underlying">Underlying</MenuItem>
            <MenuItem value="DAT-Collateral">DAT-Collateral</MenuItem>
            <MenuItem value="Bridge">Bridge</MenuItem>
            <MenuItem value="Smart-Contract">Smart-Contract</MenuItem>
            <MenuItem value="Custodial">Custodial</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth margin="normal">
          <InputLabel id="mechanism-label">Wrapping Mechanism</InputLabel>
          <Select
            labelId="mechanism-label"
            label="Wrapping Mechanism"
            value={updatedToken.mechanism ?? ''}
            name="mechanism"
            onChange={handleChange}
            MenuProps={{ disablePortal: true }}
          >
            <MenuItem value="Custodial">Custodial</MenuItem>
            <MenuItem value="Smart-Contract">Smart-Contract</MenuItem>
            <MenuItem value="Bridge">Bridge</MenuItem>
            <MenuItem value="Synthetic">Synthetic</MenuItem>
            <MenuItem value="Hybrid">Hybrid</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth margin="normal">
          <InputLabel id="risk-profile-label">Risk Profile</InputLabel>
          <Select
            labelId="risk-profile-label"
            label="Risk Profile"
            value={updatedToken.risk_profile ?? ''}
            name="risk_profile"
            onChange={handleChange}
            MenuProps={{ disablePortal: true }}
          >
            <MenuItem value="Custodial">Custodial</MenuItem>
            <MenuItem value="Smart-Contract">Smart-Contract</MenuItem>
            <MenuItem value="Bridge">Bridge</MenuItem>
            <MenuItem value="Oracle">Oracle</MenuItem>
            <MenuItem value="Treasury">Treasury</MenuItem>
            <MenuItem value="Liquidity">Liquidity</MenuItem>
            <MenuItem value="De-Peg">De-Peg</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth margin="normal">
          <InputLabel id="collateral-label">Collateral Type</InputLabel>
          <Select
            labelId="collateral-label"
            label="Collateral Type"
            value={updatedToken.collateral_type ?? ''}
            name="collateral_type"
            onChange={handleChange}
            MenuProps={{ disablePortal: true }}
          >
            <MenuItem value="Multi-Asset">Multi-Asset</MenuItem>
            <MenuItem value="Digital-Only">Digital-Only</MenuItem>
            <MenuItem value="Stablecoin-Heavy">Stablecoin-Heavy</MenuItem>
            <MenuItem value="Volatility-Hedged">Volatility-Hedged</MenuItem>
            <MenuItem value="Yield-Optimized">Yield-Optimized</MenuItem>
            <MenuItem value="Over-Collateralized">Over-Collateralized</MenuItem>
            <MenuItem value="One-to-One">1:1 Collateralized</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth margin="normal">
          <InputLabel id="underlying-label">Underlying Asset</InputLabel>
          <Select
            labelId="underlying-label"
            label="Underlying Asset"
            value={updatedToken.underlying_asset ?? ''}
            name="underlying_asset"
            onChange={handleChange}
            MenuProps={{ disablePortal: true }}
          >
            <MenuItem value="BTC">BTC</MenuItem>
            <MenuItem value="ETH">ETH</MenuItem>
            <MenuItem value="SOL">SOL</MenuItem>
            <MenuItem value="AVAX">AVAX</MenuItem>
            <MenuItem value="XRP">XRP</MenuItem>
            <MenuItem value="ADA">ADA</MenuItem>
            <MenuItem value="PECU">PECU</MenuItem>
            <MenuItem value="BCH">BCH</MenuItem>
            <MenuItem value="ETC">ETC</MenuItem>
          </Select>
        </FormControl>

        <TreasuryLockStatusFields tokenType="WRAP" tokenId={updatedToken?.id} />

        {/* Save Button */}
        <button
          style={{
            backgroundColor: 'darkgreen',
            width: '300px',
            padding: '1rem',
            color: 'white',
            margin: '2rem',
            cursor: 'pointer'
          }}
          onClick={saveToken}
        >
          Save
        </button>
      </div>
    </Dialog>
  );
}

export default EditWrapToken;
